import { dayDate, dayNumber, tripStart } from "./days.ts";
import type { When } from "./model.ts";

// Plan for a day (DESIGN.md, Views, the web's own edits; features/days.md, "Plan for a day"): a place or goal put
// on one of the trip's days from its card or from Days' Wanted, not placed, before the trip as during it; a time
// if I like; moved, or taken off its day. Plain, so the web's route (web/api.ts), Today's taps (the same `when`
// underneath) and the page (web/src/PlanDay.tsx) share it.

// The statuses something can be planned from: still wanted, or in the plan already. Placed on a day, each of these
// is in the plan (planned); what's booked, done, dropped, cancelled or one of a choice's options isn't planned here.
const PLANNABLE = new Set(["idea", "planned", "missed", "skipped"]);

// Why something can't be planned for a day here, in words for the person refused; null when it can.
export function dayRefused(item: { type: string; status: string }): string | null {
  if (item.type !== "place" && item.type !== "goal")
    return "Only a place or a goal is planned for a day on the web; ask your AI for the rest.";
  if (item.status === "booked") return "It's booked: its day changes with its booking (Edit booking).";
  if (item.status === "done") return "It's done already.";
  if (item.status === "option" || item.status === "not_chosen")
    return "It's one of a choice's options: it's planned once it's chosen.";
  if (item.status === "dropped") return "It's been dropped from the trip: ask your AI to bring it back first.";
  if (!PLANNABLE.has(item.status)) return "It can't be planned for a day here; ask your AI.";
  return null;
}

// Whether it has a day now: what "Move" and "Take off its day" are offered for.
export function hasDay(when: When | undefined): boolean {
  if (!when) return false;
  if (when.precision === "none") return false;
  if (when.precision === "part") return when.day !== undefined;
  return true;
}

// Its day now (a date or "day N"): the first day, for a span.
export function dayOf(when: When | undefined): string | null {
  if (!when || when.precision === "none") return null;
  if (when.precision === "day") return when.day;
  if (when.precision === "part") return when.day ?? null;
  if (when.precision === "days") return when.start;
  return when.start.slice(0, 10);
}

// Its time now ("10:00"), for something at a time.
export function timeOf(when: When | undefined): string | null {
  return when && (when.precision === "exact" || when.precision === "window") ? when.start.slice(11, 16) : null;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const RELATIVE = /^day ([1-9]\d{0,2})$/;
const DAY_MS = 86_400_000;
const epochDay = (date: string) => Math.round(Date.parse(`${date}T00:00:00Z`) / DAY_MS);
const fromEpochDay = (n: number) => new Date(n * DAY_MS).toISOString().slice(0, 10);
const minutes = (local: string) => Number(local.slice(11, 13)) * 60 + Number(local.slice(14, 16));

// A day moved by `by` days, of its own kind: a date stays a date, "day 3" a "day N" (never below day 1).
function shiftDay(ref: string, by: number): string | null {
  if (DATE.test(ref)) return fromEpochDay(epochDay(ref) + by);
  const n = ref.match(RELATIVE);
  if (!n) return null;
  const moved = Number(n[1]) + by;
  return moved >= 1 && moved <= 999 ? `day ${moved}` : null;
}
// How many days apart two days are, when they're of one kind (two dates, or two "day N"); null otherwise.
function daysBetween(from: string, to: string): number | null {
  if (DATE.test(from) && DATE.test(to)) return epochDay(to) - epochDay(from);
  const a = from.match(RELATIVE);
  const b = to.match(RELATIVE);
  return a && b ? Number(b[1]) - Number(a[1]) : null;
}
// A local time moved to another day and time, its length kept: `end` as far after the new start as it was.
function shiftEnd(start: string, end: string | undefined, nextStart: string): string | undefined {
  if (!end) return undefined;
  const length = (epochDay(end.slice(0, 10)) - epochDay(start.slice(0, 10))) * 1440 + minutes(end) - minutes(start);
  const at = epochDay(nextStart.slice(0, 10)) * 1440 + minutes(nextStart) + length;
  const day = Math.floor(at / 1440);
  const m = at - day * 1440;
  return `${fromEpochDay(day)}T${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

// What its `when` becomes, put on `day` (a date or "day N"), at `time` ("HH:MM", on a date only) in `tz` if given;
// with `day` null, taken off its day. A part of the day it had stays with it ("any morning", "Tue 8 Dec · morning");
// a span of days moves whole, from its first day; something at a time keeps how long it lasts. Undefined: no `when`
// at all. Shared with Today's "+ Today" and "Another day" (web/api.ts), which put it on today or take it off.
export function dayWhen(
  current: When | undefined,
  day: string | null,
  at?: { time: string; tz: string },
): When | undefined {
  if (day === null) {
    if (!current || !hasDay(current)) return current;
    return current.precision === "part" ? { precision: "part", part: current.part } : undefined;
  }
  if (at) {
    const start = `${day}T${at.time}`;
    if (current?.precision === "exact" || current?.precision === "window") {
      const end = shiftEnd(current.start, current.end, start);
      if (current.precision === "window" && end) return { precision: "window", start, end, tz: at.tz };
      return { precision: "exact", start, ...(end ? { end } : {}), tz: at.tz };
    }
    return { precision: "exact", start, tz: at.tz };
  }
  if (current?.precision === "part") return { precision: "part", day, part: current.part };
  if (current?.precision === "days") {
    const by = daysBetween(current.start, day);
    const end = by === null ? null : shiftDay(current.end, by);
    if (end) return { precision: "days", start: day, end };
  }
  return { precision: "day", day };
}

// The status it has once put on a day: in the plan (an idea, or something missed or skipped, planned again); taken
// off its day, it stays as it is (still wanted, under Days' Wanted, not placed).
export const plannedStatus = (status: string, day: string | null): string =>
  day !== null && PLANNABLE.has(status) ? "planned" : status;

// The days to pick from: the trip's own (each date, first to last, with its day number), or, while the trip has no
// dates, "day 1" on (to its last "day N", or 14 days, or the latest day anything in it is on). The day it's on now
// is among them, even outside the trip's dates (as a date, once the trip has dates). `label` as the list says it:
// "Sat 5 Dec · day 8", "Day 3".
export function dayChoices(
  dates: When | undefined,
  used: string[],
  current: string | null,
): { value: string; label: string; n: number }[] {
  const start = tripStart(dates);
  const out: { value: string; label: string; n: number }[] = [];
  if (start) {
    const last = dates?.precision === "days" && DATE.test(dates.end) ? dates.end : start;
    const count = Math.min(Math.max(1, epochDay(last) - epochDay(start) + 1), 400);
    for (let n = 1; n <= count; n++) {
      const date = dayDate(n, start) as string;
      out.push({ value: date, label: `${WEEKDAY.format(new Date(`${date}T00:00:00Z`))} · day ${n}`, n });
    }
    const now = current ? (current.match(RELATIVE) ? dayDate(Number(current.slice(4)), start) : current) : null;
    if (now && DATE.test(now) && !out.some((d) => d.value === now)) {
      const n = dayNumber(now, start) as number;
      out.push({
        value: now,
        label: `${WEEKDAY.format(new Date(`${now}T00:00:00Z`))}${n >= 1 ? ` · day ${n}` : ""}`,
        n,
      });
    }
    return out.sort((a, b) => a.value.localeCompare(b.value));
  }
  const relative = (ref: string | null | undefined) => {
    const m = ref?.match(RELATIVE);
    return m ? Number(m[1]) : 0;
  };
  const tripLast =
    dates?.precision === "days" ? relative(dates.end) : dates?.precision === "day" ? relative(dates.day) : 0;
  const count = Math.max(tripLast || 14, ...used.map(relative), relative(current));
  for (let n = 1; n <= count; n++) out.push({ value: `day ${n}`, label: `Day ${n}`, n });
  // A date it's on, though the trip has none: kept, first.
  if (current && DATE.test(current))
    out.unshift({ value: current, label: WEEKDAY.format(new Date(`${current}T00:00:00Z`)), n: 0 });
  return out;
}
const WEEKDAY = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
